import React, { useCallback, useRef, useState } from "react";
import { api } from "@/lib/api";
import { UploadCloud, Loader2, CheckCircle2, AlertCircle, Camera } from "lucide-react";
import { toast } from "sonner";
import { compressMany } from "@/lib/imageCompress";

const BATCH_SIZE = 8; // server-side parallel processing cap is 8

/**
 * Compact multi-file drop zone for quick scanning directly from Dashboard.
 * Optimized pipeline:
 *   1. Client-side image compression (12MP → ~400KB)
 *   2. Upload in batches of 8 to /api/invoices/scan-batch (server processes in parallel)
 *   3. Dedup via SHA256: identical images reuse previous OCR
 */
export default function InlineScanDrop({ reportId, onComplete }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState({ total: 0, done: 0, error: 0, deduped: 0, phase: "" });
  const [busy, setBusy] = useState(false);

  const process = useCallback(async (files) => {
    const arr = Array.from(files || []).filter((f) => {
      const t = (f.type || "").toLowerCase();
      const name = (f.name || "").toLowerCase();
      return t.startsWith("image/") || name.endsWith(".heic") || name.endsWith(".heif");
    });
    if (!arr.length) { toast.error("Aucune image détectée"); return; }

    setBusy(true);
    setProgress({ total: arr.length, done: 0, error: 0, deduped: 0, phase: "compress" });

    // Step 1: compress client-side (except HEIC which goes as-is)
    const compressed = await compressMany(arr, ({ index, total }) => {
      setProgress((p) => ({ ...p, done: index, total, phase: "compress" }));
    });
    setProgress({ total: arr.length, done: 0, error: 0, deduped: 0, phase: "upload" });

    // Step 2: upload in batches via /invoices/scan-batch
    let done = 0, err = 0, dedup = 0;
    for (let i = 0; i < compressed.length; i += BATCH_SIZE) {
      const chunk = compressed.slice(i, i + BATCH_SIZE);
      const fd = new FormData();
      chunk.forEach((f) => fd.append("files", f));
      if (reportId) fd.append("report_id", reportId);
      try {
        const { data } = await api.post("/invoices/scan-batch", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 180000,
        });
        done += data.succeeded || 0;
        err += (data.count || 0) - (data.succeeded || 0);
        dedup += data.deduped || 0;
      } catch (_e) {
        err += chunk.length;
      }
      setProgress({ total: arr.length, done, error: err, deduped: dedup, phase: "upload" });
    }

    setBusy(false);
    const msg = [
      done > 0 && `${done} facture(s) extraite(s)`,
      dedup > 0 && `${dedup} doublon(s) détecté(s)`,
      err > 0 && `${err} erreur(s)`,
    ].filter(Boolean).join(" · ");
    if (done > 0) toast.success(msg);
    else if (err > 0) toast.error(msg);
    setTimeout(() => setProgress({ total: 0, done: 0, error: 0, deduped: 0, phase: "" }), 3000);
    if (onComplete) onComplete();
  }, [reportId, onComplete]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    process(e.dataTransfer.files);
  };

  const pct = progress.total ? Math.round(((progress.done + progress.error) / progress.total) * 100) : 0;
  const phaseLabel = progress.phase === "compress" ? "Optimisation" : progress.phase === "upload" ? "Extraction IA" : "";

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      className={`ra-card p-4 mb-6 border-dashed border-2 transition-colors ${
        dragging ? "!border-[#002FA7] bg-blue-50/50 dark:bg-blue-950/20" :
        busy ? "!border-amber-400 bg-amber-50/40 dark:bg-amber-950/20" :
        "border-gray-300 dark:border-neutral-700 hover:!border-[#002FA7]"
      }`}
      data-testid="inline-scan-drop"
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        hidden
        onChange={(e) => { process(e.target.files); e.target.value = ""; }}
        data-testid="inline-scan-input"
      />
      <div className="flex flex-col sm:flex-row items-center gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {busy ? (
            <Loader2 size={28} className="text-amber-500 animate-spin shrink-0" />
          ) : (
            <UploadCloud size={28} className="text-gray-400 shrink-0" />
          )}
          <div className="min-w-0">
            <div className="font-heading font-black text-base tracking-tight">
              {busy
                ? `${phaseLabel} ${progress.done + progress.error}/${progress.total}...`
                : dragging
                  ? "Déposez pour scanner"
                  : "Glissez vos factures ici"}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {busy ? (
                <>
                  {progress.done} réussi(s){progress.error ? ` · ${progress.error} échec(s)` : ""}{progress.deduped ? ` · ${progress.deduped} doublon(s)` : ""}
                </>
              ) : (
                "Plusieurs photos à la fois · JPG, PNG, HEIC · compression auto · dédup IA"
              )}
            </div>
          </div>
        </div>
        {!busy && (
          <div className="flex gap-2 shrink-0">
            <button
              type="button"
              className="btn-secondary text-sm"
              onClick={() => inputRef.current?.click()}
              data-testid="inline-scan-browse"
            >
              <Camera size={14} /> Choisir des photos
            </button>
          </div>
        )}
        {busy && (
          <div className="w-full sm:w-48 shrink-0">
            <div className="h-2 bg-gray-200 dark:bg-neutral-700 overflow-hidden rounded">
              <div className="h-full bg-[#002FA7] transition-all" style={{ width: `${pct}%` }} />
            </div>
            <div className="text-[10px] text-gray-500 text-right mt-1">{pct}%</div>
          </div>
        )}
      </div>
      {progress.total > 0 && !busy && (
        <div className="flex items-center gap-3 text-xs text-gray-500 mt-3">
          <span className="flex items-center gap-1 text-green-600">
            <CheckCircle2 size={12} /> {progress.done} OK
          </span>
          {progress.deduped > 0 && (
            <span className="text-[#002FA7]">↻ {progress.deduped} doublon(s)</span>
          )}
          {progress.error > 0 && (
            <span className="flex items-center gap-1 text-red-600">
              <AlertCircle size={12} /> {progress.error} erreur
            </span>
          )}
        </div>
      )}
    </div>
  );
}
